import { useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Trash2, Car, Gauge, Users, RefreshCw, ImageIcon, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UploadPage = () => {
  const { getAuthHeader, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('nuovo');
  const [files, setFiles] = useState({ nuovo: null, usato: null, '4vantage': null, longrun: null });
  const [uploading, setUploading] = useState({ nuovo: false, usato: false, '4vantage': false, longrun: false });
  const [results, setResults] = useState({ nuovo: null, usato: null, '4vantage': null, longrun: null });
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e, tipo) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls'))) {
      setFiles(prev => ({ ...prev, [tipo]: droppedFile }));
      setResults(prev => ({ ...prev, [tipo]: null }));
    } else {
      toast.error('Seleziona un file Excel (.xlsx o .xls)');
    }
  }, []);

  const handleFileSelect = (e, tipo) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFiles(prev => ({ ...prev, [tipo]: selectedFile }));
      setResults(prev => ({ ...prev, [tipo]: null }));
    }
  };

  const handleUpload = async (tipo) => {
    const file = files[tipo];
    if (!file) return;

    setUploading(prev => ({ ...prev, [tipo]: true }));
    setResults(prev => ({ ...prev, [tipo]: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload async: ritorna immediatamente job_id, poi polling per progress
      const response = await axios.post(`${API}/upload/excel?tipo_listino=${tipo}`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        throw new Error('Job ID mancante nella risposta del server');
      }
      
      // Stato iniziale "in elaborazione"
      setResults(prev => ({ ...prev, [tipo]: { jobId, status: 'queued', progress: 0, progress_message: 'In coda...' } }));
      toast.info(`Upload ${tipo.toUpperCase()} avviato in background...`);
      
      // Polling ogni 1.5s fino a completed/failed
      const pollJob = async () => {
        try {
          const jobRes = await axios.get(`${API}/upload/jobs/${jobId}`, { headers: getAuthHeader() });
          const job = jobRes.data;
          setResults(prev => ({ ...prev, [tipo]: { jobId, ...job } }));
          
          if (job.status === 'completed') {
            const r = job.result || {};
            toast.success(`✓ Upload ${tipo.toUpperCase()} completato: ${r.vehicles_added || 0} aggiunti, ${r.vehicles_updated || 0} aggiornati`);
            setFiles(prev => ({ ...prev, [tipo]: null }));
            setUploading(prev => ({ ...prev, [tipo]: false }));
            return;
          }
          if (job.status === 'failed') {
            toast.error(`✗ Upload ${tipo.toUpperCase()} fallito: ${job.error || 'errore sconosciuto'}`);
            setUploading(prev => ({ ...prev, [tipo]: false }));
            return;
          }
          // Continua polling
          setTimeout(pollJob, 1500);
        } catch (pollErr) {
          console.error('Errore polling job:', pollErr);
          setTimeout(pollJob, 3000);  // ritenta dopo 3s
        }
      };
      pollJob();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Errore durante l\'upload';
      
      if (error.response?.status === 401) {
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        logout();
        navigate('/login');
        return;
      }
      
      setResults(prev => ({ 
        ...prev, 
        [tipo]: { success: false, error: errorMessage } 
      }));
      toast.error('Errore durante l\'importazione: ' + errorMessage);
      setUploading(prev => ({ ...prev, [tipo]: false }));
    }
  };

  const handleDeleteByType = async (tipo) => {
    if (!window.confirm(`Sei sicuro di voler eliminare tutti i veicoli ${tipo === 'usato' ? 'usati' : 'nuovi'}? Questa azione è irreversibile.`)) {
      return;
    }

    try {
      // Delete vehicles by tipo
      const response = await axios.delete(`${API}/vehicles?tipo=${tipo}`, { headers: getAuthHeader() });
      toast.success(response.data.message || 'Veicoli eliminati');
    } catch (error) {
      toast.error('Errore nell\'eliminazione dei veicoli');
    }
  };

  const UploadSection = ({ tipo, icon: Icon, title, description, color }) => (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 cursor-pointer",
          isDragging 
            ? `border-${color} bg-${color}/5` 
            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50",
          files[tipo] && `border-${color} bg-${color}/5`
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, tipo)}
        onClick={() => document.getElementById(`file-input-${tipo}`).click()}
        data-testid={`drop-zone-${tipo}`}
      >
        <input
          id={`file-input-${tipo}`}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={(e) => handleFileSelect(e, tipo)}
          data-testid={`file-input-${tipo}`}
        />
        
        {files[tipo] ? (
          <div className="space-y-3">
            <FileSpreadsheet className={`w-12 h-12 mx-auto text-${color}`} />
            <p className="font-medium text-slate-800">{files[tipo].name}</p>
            <p className="text-sm text-slate-500">
              {(files[tipo].size / 1024).toFixed(1)} KB
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setFiles(prev => ({ ...prev, [tipo]: null }));
              }}
            >
              Rimuovi
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className={`w-16 h-16 mx-auto rounded-full bg-${color}/10 flex items-center justify-center`}>
              <Icon className={`w-8 h-8 text-${color}`} />
            </div>
            <div>
              <p className="font-medium text-slate-800">
                Trascina qui il file {title}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                oppure clicca per sfogliare
              </p>
            </div>
          </div>
        )}
      </div>

      <Button
        className={cn("w-full", `bg-${color} hover:bg-${color}/90`)}
        disabled={!files[tipo] || uploading[tipo]}
        onClick={() => handleUpload(tipo)}
        data-testid={`upload-btn-${tipo}`}
        style={{ backgroundColor: color === 'primary' ? 'hsl(220, 70%, 50%)' : color === 'emerald-600' ? '#059669' : '#7c3aed' }}
      >
        {uploading[tipo] ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Importazione in corso...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            Importa {title}
          </>
        )}
      </Button>

      {/* Upload Result / Progress */}
      {results[tipo] && (() => {
        const r = results[tipo];
        // Job in corso (status presente da polling)
        const isJobRunning = r.status && ['queued', 'parsing', 'writing'].includes(r.status);
        const isJobCompleted = r.status === 'completed';
        const isJobFailed = r.status === 'failed' || r.success === false;
        
        if (isJobRunning) {
          return (
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid={`upload-progress-${tipo}`}>
              <div className="flex items-start gap-3 mb-3">
                <Loader2 className="w-5 h-5 text-blue-600 mt-0.5 animate-spin" />
                <div className="flex-1">
                  <p className="font-medium text-blue-800">Elaborazione in corso...</p>
                  <p className="text-sm text-blue-700 mt-1">{r.progress_message || 'In coda...'}</p>
                </div>
                <span className="text-sm font-bold text-blue-700">{r.progress || 0}%</span>
              </div>
              {/* Progress bar */}
              <div className="w-full bg-blue-100 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500 ease-out"
                  style={{ width: `${r.progress || 0}%` }}
                />
              </div>
              {r.total_rows > 0 && (
                <p className="text-xs text-blue-600 mt-2">
                  {r.rows_processed || 0} / {r.total_rows} righe processate
                </p>
              )}
            </div>
          );
        }
        
        if (isJobCompleted) {
          const result = r.result || {};
          return (
            <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid={`upload-result-${tipo}`}>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-800">Importazione completata!</p>
                  <ul className="text-sm text-emerald-700 mt-1 space-y-1">
                    <li>• Veicoli aggiunti: {result.vehicles_added || 0}</li>
                    <li>• Veicoli aggiornati: {result.vehicles_updated || 0}</li>
                    {(result.vehicles_skipped || 0) > 0 && (
                      <li>• Veicoli saltati: {result.vehicles_skipped}</li>
                    )}
                    {(result.vehicles_sold || 0) > 0 && (
                      <li>• Veicoli marcati come VENDUTO: {result.vehicles_sold}</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        
        if (isJobFailed) {
          return (
            <div className="p-4 rounded-lg bg-red-50 border border-red-200" data-testid={`upload-result-${tipo}`}>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="font-medium text-red-800">Errore nell'importazione</p>
                  <p className="text-sm text-red-700 mt-1">{r.error || r.progress_message || 'Errore sconosciuto'}</p>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Instructions */}
      <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-600">
        <p className="font-medium text-slate-700 mb-2">{description}</p>
        <p>Il file deve contenere le colonne delle quotazioni nel formato:</p>
        <code className="mono bg-slate-200 px-1 rounded text-xs">canone_10000_36, canone_15000_48, ...</code>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="upload-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Importa Listini
        </h1>
        <p className="text-slate-500 mt-1">Carica i file Excel con le quotazioni dei veicoli</p>
      </div>

      {/* Upload Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger value="nuovo" className="flex items-center gap-2" data-testid="upload-tab-nuovo">
                <Car className="w-4 h-4" />
                NUOVO
              </TabsTrigger>
              <TabsTrigger value="usato" className="flex items-center gap-2" data-testid="upload-tab-usato">
                <Gauge className="w-4 h-4" />
                USATO
              </TabsTrigger>
              <TabsTrigger value="4vantage" className="flex items-center gap-2" data-testid="upload-tab-4vantage">
                <Users className="w-4 h-4" />
                4VANTAGE
              </TabsTrigger>
              <TabsTrigger value="longrun" className="flex items-center gap-2" data-testid="upload-tab-longrun">
                <Gauge className="w-4 h-4" />
                LONGRUN
              </TabsTrigger>
            </TabsList>

            <TabsContent value="nuovo">
              <UploadSection 
                tipo="nuovo"
                icon={Car}
                title="Veicoli Nuovi"
                description="File quote_stock.xls - Listino veicoli nuovi con quotazioni standard"
                color="primary"
              />
            </TabsContent>

            <TabsContent value="usato">
              <UploadSection 
                tipo="usato"
                icon={Gauge}
                title="Veicoli Usati"
                description="File quote_rinoleggio.xls - Listino veicoli usati con km, immatricolazione e foto"
                color="emerald-600"
              />
            </TabsContent>

            <TabsContent value="4vantage">
              <div className="space-y-4">
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <h4 className="font-medium text-violet-800 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Listino 4VANTAGE
                  </h4>
                  <p className="text-sm text-violet-700 mt-1">
                    Quotazioni speciali per <strong>dipendenti</strong> e <strong>P.IVA forfettarie</strong>.
                    Questo file aggiorna le quotazioni 4Vantage sui veicoli NUOVI già esistenti.
                  </p>
                </div>
                <UploadSection 
                  tipo="4vantage"
                  icon={Users}
                  title="4VANTAGE"
                  description="File quote_stock_4vantage.xls - Quotazioni speciali per dipendenti/forfettari"
                  color="violet-600"
                />
              </div>
            </TabsContent>

            <TabsContent value="longrun">
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" style={{ borderColor: '#8B4513', backgroundColor: '#fdf5ef' }}>
                  <h4 className="font-medium flex items-center gap-2" style={{ color: '#6b3410' }}>
                    <Gauge className="w-4 h-4" />
                    Listino LONGRUN (Usato)
                  </h4>
                  <p className="text-sm mt-1" style={{ color: '#6b3410' }}>
                    Quotazione speciale a <strong>48 mesi - Km illimitati</strong> per veicoli usati.
                    Il file viene associato ai veicoli del listino USATO tramite il <code>vehicle_id</code> (prima colonna).
                  </p>
                </div>
                <UploadSection 
                  tipo="longrun"
                  icon={Gauge}
                  title="LONGRUN"
                  description="File quote_rinoleggio_longrun.xls - Quotazione 48 mesi km illimitati"
                  color="amber-700"
                />
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Sync Immagini Drive */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-blue-600" />
                Sincronizza Immagini Drive
              </CardTitle>
              <CardDescription>
                Aggiorna le immagini dei veicoli dalla cartella Google Drive
              </CardDescription>
            </div>
            <Button
              onClick={async () => {
                try {
                  toast.info('Sincronizzazione in corso...');
                  const res = await axios.post(`${API}/drive-images/sync`, {}, { headers: getAuthHeader() });
                  if (res.data.success) {
                    toast.success(`Sync completata: ${res.data.images_indexed} immagini indicizzate`);
                  } else {
                    toast.error(`Errore: ${res.data.error}`);
                  }
                } catch (err) {
                  toast.error('Errore durante la sincronizzazione');
                }
              }}
              className="flex items-center gap-2"
              data-testid="sync-drive-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Sincronizza
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Format Guide */}
      <Card>
        <CardHeader>
          <CardTitle>Formato File Excel</CardTitle>
          <CardDescription>
            Struttura richiesta per i file di importazione
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2">Colonne veicolo:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <code className="mono bg-slate-200 px-1 rounded">vehicleid</code> - ID univoco</li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">makename</code> - Marca</li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">modelname</code> - Modello</li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">description</code> - Versione</li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">fuel</code> - Carburante</li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">transmission</code> - Cambio</li>
              </ul>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium text-slate-800 mb-2">Colonne quotazioni:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <code className="mono bg-slate-200 px-1 rounded">canone_10000_36</code></li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">canone_15000_48</code></li>
                <li>• <code className="mono bg-slate-200 px-1 rounded">canone_20000_60</code></li>
                <li>• ... (tutte le 12 combinazioni)</li>
              </ul>
            </div>
          </div>

          <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
            <h4 className="font-medium text-emerald-800 mb-1">Solo per USATO:</h4>
            <ul className="text-sm text-emerald-700 space-y-1">
              <li>• <code className="mono bg-emerald-200 px-1 rounded">km</code> - Chilometri</li>
              <li>• <code className="mono bg-emerald-200 px-1 rounded">registrationdate</code> - Data immatricolazione</li>
              <li>• <code className="mono bg-emerald-200 px-1 rounded">link2lifeimage</code> - Link foto veicolo</li>
            </ul>
          </div>

          <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
            <h4 className="font-medium text-violet-800 mb-1">Solo per 4VANTAGE:</h4>
            <p className="text-sm text-violet-700 mb-2">
              Il file 4Vantage usa una struttura semplificata con una sola colonna canone:
            </p>
            <ul className="text-sm text-violet-700 space-y-1">
              <li>• <code className="mono bg-violet-200 px-1 rounded">vehicleid</code> - ID del veicolo (Colonna A)</li>
              <li>• <code className="mono bg-violet-200 px-1 rounded">makename</code> - Marca</li>
              <li>• <code className="mono bg-violet-200 px-1 rounded">modelname</code> - Modello</li>
              <li>• <code className="mono bg-violet-200 px-1 rounded">canone_4vantage</code> - Canone unico 4Vantage (Colonna AM)</li>
            </ul>
            <p className="text-xs text-violet-600 mt-2 italic">
              Il sistema usa il vehicleid per agganciare i veicoli esistenti. Se non trovato, propaga ai veicoli gemelli (stessa marca/modello).
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-600 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            Zona Pericolosa
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <p className="font-medium text-red-800">Elimina veicoli NUOVI</p>
                <p className="text-sm text-red-600">Rimuove tutti i veicoli nuovi</p>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => handleDeleteByType('nuovo')}
                data-testid="delete-nuovo-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina
              </Button>
            </div>
            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div>
                <p className="font-medium text-red-800">Elimina veicoli USATI</p>
                <p className="text-sm text-red-600">Rimuove tutti i veicoli usati</p>
              </div>
              <Button 
                variant="destructive" 
                size="sm"
                onClick={() => handleDeleteByType('usato')}
                data-testid="delete-usato-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Elimina
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadPage;
